// Org details → Onboarding tab: the welcome offer this org got at signup
// (SS-13), with Disable / Enable / Extend for admin + support.
import React, {useEffect, useState} from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, TextField, Typography
} from '@mui/material';

import {updateTenantOffer} from '../../actions/welcomeOffer';
import {dateFormat} from '../../lib/utils';
import {OFFER_EXTEND_DAYS_MAX, OFFER_EXTEND_DAYS_MIN, offerStatus, validateExtendDays} from '../../lib/welcomeOffer';

const STATUS_CHIP = {
  active: {label: 'Active', color: 'success'},
  expired: {label: 'Expired', color: 'default'},
  disabled: {label: 'Disabled', color: 'warning'}
};

const errorText = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

const when = (value) => (value ? dateFormat(value) : '--');

const Row = ({label, children}) => (
  <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5}}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={500} component="div">{children}</Typography>
  </Box>
);

const WelcomeOffer = ({tenantId, offer: initialOffer, canManage}) => {
  const [offer, setOffer] = useState(initialOffer || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDays, setExtendDays] = useState('7');
  const [extendError, setExtendError] = useState('');

  useEffect(() => setOffer(initialOffer || null), [tenantId, initialOffer]);

  const save = (body) => {
    setBusy(true);
    setError('');
    return updateTenantOffer(tenantId, body)
      .then((res) => {
        if (res && res.success === false) {
          throw new Error(res.error || res.message || 'Failed to update the offer');
        }
        if (res && res.offer) {
          setOffer(res.offer);
        }
        setConfirmDisable(false);
        setExtendOpen(false);
      })
      .catch((err) => setError(errorText(err, 'Failed to update the offer')))
      .finally(() => setBusy(false));
  };

  const submitExtend = () => {
    const invalid = validateExtendDays(extendDays);
    setExtendError(invalid);
    if (invalid) {
      return;
    }
    save({enabled: !!offer.enabled, extendDays: Number(extendDays)});
  };

  if (!offer) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600}>Welcome offer</Typography>
          <Typography variant="body2" color="text.secondary">No welcome offer</Typography>
        </CardContent>
      </Card>
    );
  }

  const chip = STATUS_CHIP[offerStatus(offer)];
  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
          <Typography variant="subtitle1" fontWeight={600}>Welcome offer</Typography>
          <Chip label={chip.label} color={chip.color} size="small" variant="outlined" />
        </Box>
        {error ? <Alert severity="error" sx={{mb: 1}} onClose={ () => setError('') }>{error}</Alert> : null}
        <Row label="Discount">{offer.percentOff ? `${offer.percentOff}% off first purchase` : '--'}</Row>
        <Row label="Code">{offer.code || '--'}</Row>
        <Row label="Length">{offer.days ? `${offer.days} days` : '--'}</Row>
        <Row label="Granted">{when(offer.grantedAt)}</Row>
        <Row label="Ends">{when(offer.endsAt)}</Row>
        {canManage ?
          <Box sx={{display: 'flex', gap: 1, mt: 1}}>
            {offer.enabled ?
              <Button size="small" color="error" disabled={ busy } onClick={ () => setConfirmDisable(true) }>
                Disable
              </Button>
              :
              <Button size="small" disabled={ busy } onClick={ () => save({enabled: true, extendDays: 0}) }>
                Enable
              </Button>}
            <Button
              size="small"
              disabled={ busy }
              onClick={ () => {
                setExtendError('');
                setExtendOpen(true);
              } }
            >
              Extend…
            </Button>
          </Box>
          : null}
      </CardContent>

      <Dialog open={ confirmDisable } onClose={ busy ? undefined : () => setConfirmDisable(false) } maxWidth="xs" fullWidth>
        <DialogTitle>Disable welcome offer?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The org stops seeing the offer and it no longer applies to their first purchase. You can enable it
            again later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={ () => setConfirmDisable(false) } disabled={ busy }>Cancel</Button>
          <Button color="error" variant="contained" disabled={ busy } onClick={ () => save({enabled: false, extendDays: 0}) }>
            {busy ? <CircularProgress size={ 18 } /> : 'Disable offer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ extendOpen } onClose={ busy ? undefined : () => setExtendOpen(false) } maxWidth="xs" fullWidth>
        <DialogTitle>Extend welcome offer</DialogTitle>
        <DialogContent>
          <TextField
            label="Extra days"
            type="number"
            value={ extendDays }
            onChange={ (e) => setExtendDays(e.target.value) }
            error={ !!extendError }
            helperText={ extendError || `${OFFER_EXTEND_DAYS_MIN} to ${OFFER_EXTEND_DAYS_MAX} days, added to the end date` }
            inputProps={{min: OFFER_EXTEND_DAYS_MIN, max: OFFER_EXTEND_DAYS_MAX}}
            fullWidth
            variant="standard"
            disabled={ busy }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={ () => setExtendOpen(false) } disabled={ busy }>Cancel</Button>
          <Button variant="contained" disabled={ busy } onClick={ submitExtend }>
            {busy ? <CircularProgress size={ 18 } /> : 'Extend'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default WelcomeOffer;
